import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Rocket, Loader2 } from 'lucide-react';
import { ADMIN_PALETTE } from './adminDesignSystem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin modal for rolling out a new agreement version.
 *
 * Body is optional — leave blank to render straight from the current code
 * template (`agreement_templates.py`) at the target version string. Paste a
 * custom body to override (e.g. after a lawyer review returns a new draft).
 */
export function AgreementRolloutModal({ kind, currentVersion, headers, onClose, onRolledOut }) {
  const [version, setVersion] = useState('');
  const [changelogNote, setChangelogNote] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setVersion(''); setChangelogNote(''); setBody(''); }, [kind]);

  const submit = async () => {
    if (!version.trim()) {
      toast.error('Pick a version string, e.g. v2.2');
      return;
    }
    if (version.trim() === currentVersion) {
      toast.error(`v${currentVersion} is already live. Bump the number.`);
      return;
    }
    if (!changelogNote.trim()) {
      toast.error('Add a short changelog note so signers see what changed.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/agreement-versions/rollout`, {
        kind,
        version: version.trim(),
        changelog_note: changelogNote.trim(),
        body: body.trim() || undefined,
      }, { headers });
      toast.success(`Version ${res.data?.version || version.trim()} is live. Notifying signers…`);
      onRolledOut?.(res.data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rollout failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="agreement-rollout-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(6px)', zIndex: 10002,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 640, width: '100%', background: ADMIN_PALETTE.surface,
          borderRadius: 20, boxShadow: '0 30px 60px rgba(0,0,0,0.32)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          maxHeight: '90vh',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', borderBottom: `1px solid ${ADMIN_PALETTE.border}`,
        }}>
          <Rocket size={16} color={ADMIN_PALETTE.brandRed} strokeWidth={2.2} />
          <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 800, flex: 1, color: ADMIN_PALETTE.ink }}>
            Roll out new {kind === 'venue' ? 'Venue' : 'Host'} agreement
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid="agreement-rollout-close"
            aria-label="Close"
            style={{
              background: ADMIN_PALETTE.surfaceMuted, border: `1px solid ${ADMIN_PALETTE.border}`,
              borderRadius: 10, width: 32, height: 32, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              color: ADMIN_PALETTE.ink,
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 12.5, color: ADMIN_PALETTE.inkMuted }}>
            Current live version: <strong style={{ color: ADMIN_PALETTE.ink }}>{currentVersion || '—'}</strong>. Every user who signed a previous version will be notified via email + push + in-app popup. A 3-day grace begins now — after that, features are hard-gated until they re-sign.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: ADMIN_PALETTE.inkSoft, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
              New version string
            </label>
            <input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g. v2.2"
              data-testid="agreement-rollout-version"
              style={{
                padding: '10px 12px', borderRadius: 10,
                border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surfaceMuted,
                fontSize: 14, color: ADMIN_PALETTE.ink,
              }}
            />
            <span style={{ fontSize: 11.5, color: ADMIN_PALETTE.inkMuted }}>
              Must be unique for this kind. Convention: bump minor for wording tweaks, major for structural changes.
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: ADMIN_PALETTE.inkSoft, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
              Changelog note (shown to signers)
            </label>
            <textarea
              value={changelogNote}
              onChange={(e) => setChangelogNote(e.target.value)}
              rows={2}
              placeholder="e.g. Legal counsel review — clarified GDPR data-processor language in Section 7."
              data-testid="agreement-rollout-changelog"
              style={{
                padding: '10px 12px', borderRadius: 10,
                border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surfaceMuted,
                fontSize: 13.5, color: ADMIN_PALETTE.ink, resize: 'vertical', minHeight: 60,
                lineHeight: 1.4, fontFamily: 'inherit',
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: ADMIN_PALETTE.inkSoft, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
              Custom body (optional)
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder="Leave blank to render from the code template at this version, or paste the lawyer-approved body here to override."
              data-testid="agreement-rollout-body"
              style={{
                padding: '10px 12px', borderRadius: 10,
                border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surfaceMuted,
                fontSize: 12.5, color: ADMIN_PALETTE.ink, resize: 'vertical', minHeight: 180,
                lineHeight: 1.5, fontFamily: "'Inria Serif', serif",
              }}
            />
            <span style={{ fontSize: 11.5, color: ADMIN_PALETTE.inkMuted }}>
              Pasted bodies are stored verbatim and shown to every new signer of this version.
            </span>
          </div>
        </div>

        <div style={{
          display: 'flex', gap: 8, padding: 12,
          borderTop: `1px solid ${ADMIN_PALETTE.border}`,
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 12,
              border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surface,
              color: ADMIN_PALETTE.ink, fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="agreement-rollout-submit"
            style={{
              flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '10px 14px', borderRadius: 12, border: 'none',
              background: ADMIN_PALETTE.ink, color: '#FFF',
              fontWeight: 800, fontSize: 13, cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? <Loader2 size={13} className="address-loader-spin" /> : <Rocket size={13} />}
            {submitting ? 'Rolling out…' : 'Roll out & notify'}
          </button>
        </div>
      </div>
    </div>
  );
}
